import type { InternalNetworkAttackPathEventDto } from '@terra/shared-contracts-internal-network';

const isDiscoveryMasqueradingAsWeakness = (
  event: InternalNetworkAttackPathEventDto,
): boolean => event.kind === 'WEAKNESS_EXPLOITED' && !event.exploitsWeakness;

const resolveWeaknessHopKey = (
  event: Extract<
    InternalNetworkAttackPathEventDto,
    { kind: 'WEAKNESS_EXPLOITED' }
  >,
): string =>
  [
    event.hopIndex ?? '',
    event.technique ?? '',
    event.sourceHostname?.trim().toLowerCase() ?? '',
    event.hostname?.trim().toLowerCase() ?? '',
  ].join('\0');

const remapSourceEventIds = ({
  sourceEventIds,
  survivorByDroppedId,
  selfId,
}: {
  sourceEventIds: string[];
  survivorByDroppedId: Map<string, string>;
  selfId: string;
}): string[] => [
  ...new Set(
    sourceEventIds
      .map((sourceId) => survivorByDroppedId.get(sourceId) ?? sourceId)
      .filter((sourceId) => sourceId !== selfId),
  ),
];

const resolveCollapsedWeaknessStatus = (
  group: Array<
    Extract<InternalNetworkAttackPathEventDto, { kind: 'WEAKNESS_EXPLOITED' }>
  >,
): InternalNetworkAttackPathEventDto['status'] => {
  const latest = group[group.length - 1];

  if (latest?.status === 'SUCCEEDED') {
    return 'SUCCEEDED';
  }

  if (group.some((member) => member.status === 'FAILED')) {
    return 'FAILED';
  }

  return latest?.status ?? 'IN_PROGRESS';
};

export const collapseAttackPathRetryEvents = (
  events: InternalNetworkAttackPathEventDto[],
): InternalNetworkAttackPathEventDto[] => {
  const ordered = [...events].sort(
    (left, right) =>
      new Date(left.occurredAt).getTime() -
      new Date(right.occurredAt).getTime(),
  );
  const visible = ordered.filter(
    (event) => !isDiscoveryMasqueradingAsWeakness(event),
  );
  const groups = new Map<
    string,
    Array<
      Extract<InternalNetworkAttackPathEventDto, { kind: 'WEAKNESS_EXPLOITED' }>
    >
  >();

  for (const event of visible) {
    if (event.kind !== 'WEAKNESS_EXPLOITED') {
      continue;
    }

    const key = resolveWeaknessHopKey(event);
    const group = groups.get(key) ?? [];

    group.push(event);
    groups.set(key, group);
  }

  const survivorByDroppedId = new Map<string, string>();

  for (const group of groups.values()) {
    const survivorId = group[0]?.id;

    if (!survivorId) {
      continue;
    }

    for (const event of group.slice(1)) {
      survivorByDroppedId.set(event.id, survivorId);
    }
  }

  const collapsed: InternalNetworkAttackPathEventDto[] = [];

  for (const event of visible) {
    if (event.kind !== 'WEAKNESS_EXPLOITED') {
      collapsed.push({
        ...event,
        sourceEventIds: remapSourceEventIds({
          sourceEventIds: event.sourceEventIds,
          survivorByDroppedId,
          selfId: event.id,
        }),
      });
      continue;
    }

    const group = groups.get(resolveWeaknessHopKey(event));
    const first = group?.[0];
    const latest = group?.[group.length - 1];

    if (!first || !latest || first.id !== event.id) {
      continue;
    }

    const retryCount =
      group.reduce((total, member) => total + 1 + (member.retryCount ?? 0), 0) -
      1;
    const status = resolveCollapsedWeaknessStatus(group);

    collapsed.push({
      ...latest,
      id: first.id,
      sourceEventIds: remapSourceEventIds({
        sourceEventIds: first.sourceEventIds,
        survivorByDroppedId,
        selfId: first.id,
      }),
      status,
      isProven: status === 'SUCCEEDED' && latest.isProven,
      retryCount,
      occurredAt: first.occurredAt,
    });
  }

  return collapsed;
};
